const article = {
	slug: "your-tech-exec-is-a-user-too",
	title: "Your Tech Exec Is a User Too. Are You Designing for Them?",
	date: "May 2025",
	updated: null,
	description: "A human-centered design course reframed senior technology leaders as users worth designing for, and turned empathy into a two-way street between teams and their leaders.",
	keywords: ["human-centered design", "leadership", "empathy", "servant leadership"],
	tldr: "Great products start with understanding users, and your executives are users too. Empathy in leadership runs both directions.",
	readingTime: null,
	provenance: "Adapted from my LinkedIn post, May 2025.",
	provenanceUrl: null,
	series: null,
	artifact: null,
	markdown: `As part of a recent Human-Centered Design certification, our perspective on 'users' expanded to include a critical but often overlooked group: Senior Technology Leaders. Not just the role, but the human behind it.

I have had good luck working with great leaders, and I have also seen what happens when that empathy is missing.

This certification course reminded me that building great products starts with understanding users, and that includes your stakeholders.

We learned about (among other things):
- Throwing away assumptions
- Staying open to the possibilities
- Cultivating curiosity

## Designing for the VP

To put this into practice, we created a persona of a fictional VP of Engineering at a Fortune 50 company. Drawing from her character, we explored what a leader like her might experience and expect from their team.

This is a snippet of her Empathy map canvas:

1. Says:
- "We need to move faster, what's the blocker?"
- "I need to see tangible results, not just constant effort"

2. Thinks:
- "Do they understand the big picture or are we just spinning wheels with busy work?"
- "How do I scale myself without losing control?"

3. Does:
- Jumps between high-level strategy and deep-dive engineering discussions
- Spot-checks product decisions and design choices
- Helps build a collaborative engineering environment

4. Feels:
- Frustrated by bureaucracy
- Proud when the team delivers independently
- Wary of micromanaging but anxious about quality
- Energized by quick wins and bold thinking

## What legacy code taught me

Over the past year, I have had the opportunity to lead a large, legacy codebase. That came with high expectations and several moving pieces.

I struggled, early on. Initiatives took time to gain traction. Our team kept slipping into POC (proof of concept) circles. We built things that impressed but couldn't ship or scale on cost and metrics.

Momentum was hard-earned, and delivery often felt just out of reach. It was frustrating. And humbling.

But this course's exercise helped me realize that just like teams need support, so do leaders. Empathy is a two-way street.

## Empathy runs both ways

As servant leaders in agile teams, we often focus on supporting our teams. But there's strength in also understanding our leaders. Sometimes that means having the courage to say: "Let us take this off your plate. We've got it." Fully aware that it is easier said than done.

This idea of leadership being a shared responsibility resonated during our recent Town Hall, where our VP highlighted the application of psychological safety for teams. He is one of the few leaders I have seen who walks the talk. He empowers individuals and teams by entrusting them with challenging projects while building a supportive and open environment.

This Human-centered design course gave me new tools to build stuff, but more importantly, it gave me a perspective.

Leadership is complicated by design. And good design begins with empathy.`,
};

export default article;
